import { USER_IDENTIFY_TRAITS } from '@marketplace/helpers/segment/identify.js'
import { LANGUAGES_WITHOUT_DEFAULT } from '@shared/config/lang.js'
import Cookies from 'js-cookie'
import isArray from 'lodash/isArray'

import { pageView } from './pageview.js'

if (typeof window.getOCSessionData === 'function') {
    window.getOCSessionData()
}

const localeLessPropertiesPath = () => {
    const languages = LANGUAGES_WITHOUT_DEFAULT
    const url = new URL(window.location.href)
    const urlParts = url.pathname.split('/')
    const activeLocale = urlParts[1]

    if (languages.includes(activeLocale) && activeLocale.length === 2) {
        // if url has a 2 length lang after slash, consider it a locale.
        urlParts.splice(1, 1)
        return urlParts.length === 1 ? '/' : urlParts.join('/')
    }

    return url.pathname
}

const OCSessionData = () => {
    return {
        ...window.OCSessionData,
        url: window.location.href.toLowerCase(),
        path: localeLessPropertiesPath(),
    }
}

export { pageView }

/**
 * Fetches the latest event source cookies
 *
 * @returns {object}
 */
export const getDiscoveryCookies = () => {
    return {
        discoveryPath: Cookies.get('discovery_path') || 'direct',
        discoveryAttributes: Cookies.get('discovery_attributes'),
        discoveryQuery: Cookies.get('discovery_query'),
        discoveryCategoryLevel: Cookies.get('discovery_category_level'),
    }
}

// an abstract/default way to track clicking.
// eventAction is a String.
// eventProperties is an Object it will be merged with properties.
// don't forget to include at least "category" inside eventProperties.
export const trackClick = (
    eventAction,
    eventProperties,
    eventPrefix = 'Click'
) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    if (window.Store?.env === 'development') {
        if (!eventProperties.category) {
            throw new Error(
                'eventProperties should include at least the category property.'
            )
        }
    }

    const routeDepth = window?.Store?.currentCategoryDepth

    const action = `${eventPrefix} ${eventAction}`
    const properties = {
        ...eventProperties,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        // direct check here only
        window.analytics.track(action, properties)
    }
}

export const track = (eventAction, eventProperties) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    if (window.Store?.env === 'development') {
        if (!eventProperties.category) {
            throw new Error(
                'eventProperties should include at least the category property.'
            )
        }
    }

    const routeDepth = window?.Store?.currentCategoryDepth

    const properties = {
        ...eventProperties,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackLoggedOut = (eventAction, eventProperties) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    if (window.Store?.env === 'development') {
        if (!eventProperties.category) {
            throw new Error(
                'eventProperties should include at least the category property.'
            )
        }
    }

    const routeDepth = window?.Store?.currentCategoryDepth

    const properties = {
        ...eventProperties,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    window.analytics.track(eventAction, properties)
}

export const trackCartMinimumOrder = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    window.analytics.track(payload[0], {
        category: 'Checkout',
        storefront: payload[1],
        minimum: payload[2],
        ...OCSessionData(),
    })
}

export const trackOrderCompleted = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    window.analytics.track('Order Completed Frontend', {
        ...payload,
        ...OCSessionData(),
    })
}

export const trackOpenQuickBuyModal = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    window.analytics.track('Product Quick Buy Open', {
        category: payload?.category || 'Storefront',
        listingId: payload.listingId,
        product: payload.product,
        storefront: payload.storefront,
        position: payload.index,
        locale: window.Store?.locale || 'unknown',
        ...OCSessionData(),
    })
}

export const trackCloseQuickBuyModal = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    window.analytics.track('Product Quick Buy Close', {
        category: payload?.category || 'Storefront',
        product: payload.product,
        listingId: payload.listingId,
        storefront: payload.storefront,
        position: payload.index,
        locale: window.Store?.locale || 'unknown',
        ...OCSessionData(),
    })
}

export const trackBuyQuickBuyModal = (payload, extraPayload = {}) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const name = payload.name || 'in Modal'

    window.analytics.track(`Product Quick Buy Add to Cart ${name}`, {
        category: payload?.category || 'Storefront',
        listingId: payload.listingId,
        position: payload.index,
        brandSlug: payload.brandSlug,
        isFavorite: payload.isFavorite,
        ...getDiscoveryCookies(),
        ...extraPayload,
        ...OCSessionData(),
    })
}

export const trackRetailerSignupStep1Complete = () => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    window.analytics.track('Retailer Signup Step 1 completed - basic account', {
        ...OCSessionData(),
    })
}

export const trackRetailerSignupCompleted = (id) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    window.analytics.track('Retailer Signup Completed Frontend', {
        transaction_id: id, // for Google Ads binding
        order_id: id, // for Google Ads binding
        ...OCSessionData(),
    })
}

export const trackSupplierSignupStep = (stepNumber) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    if (stepNumber <= 3) {
        // after step 3 there should be a user already, until then identify as SystemUser
        identifySystemUser()
    }

    window.analytics.track(`Supplier Registration Step ${stepNumber} reached`, {
        ...OCSessionData(),
        // overwrite session data forlogged out
        ...(stepNumber <= 3 && {
            realUserId: 'SystemUser',
            accountId: 'SystemUser',
        }),
    })
}

export const trackRetailerSignupValidationErrors = ({
    country,
    email,
    phone,
    errors,
}) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    identifySystemUser()

    window.analytics.track('Retailer Registration Validation Error', {
        country,
        email,
        phone,
        errors,
        ...OCSessionData(),
        // overwrite  session data forlogged out
        realUserId: 'SystemUser',
        accountId: 'SystemUser',
    })
}

export const trackSupplierSignupValidationErrors = ({
    email,
    phone,
    errors,
}) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    identifySystemUser()

    window.analytics.track('Retailer Registration Validation Error', {
        email,
        phone,
        errors,
        ...OCSessionData(),
        // overwrite  session data forlogged out
        realUserId: 'SystemUser',
        accountId: 'SystemUser',
    })
}

export const trackStickyFooterRegisterClick = () => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
}

export const trackHomepageRegisterButtonClick = () => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
}

export const identifyWithTraits = (account) => {
    // will be depricated soon (use promise based identify)
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    if (account.id) {
        window.analytics.identify(account.id, {
            ...USER_IDENTIFY_TRAITS(),
            ...OCSessionData(),
        })
    }
}
/**
 * A promise used to identify a user before logging in (signup)
 *
 * @param {object} account
 */
export const identifyAccountWithTraitsPromise = (account) => {
    return new Promise(function (resolve, reject) {
        if (!account || window.Store.isExcludedFromTracking) {
            reject()
            return
        }

        window.analytics.identify(account.id, {
            ...account,
            ...OCSessionData(),
        })

        setTimeout(() => {
            // slight delay so identify actually works
            resolve()
        }, 100)
    })
}

/**
 * A promise used to identify a user when we are sure they are logged in
 *
 * @param {boolean} delayed
 */
export const identifyWithTraitsPromise = (delayed = true) => {
    return new Promise(function (resolve, reject) {
        const userData = USER_IDENTIFY_TRAITS()

        if (!userData?.id || window.Store.isExcludedFromTracking) {
            reject({
                message: 'is excluded from tracking',
                userData,
                isExcludedFromTracking: window.Store.isExcludedFromTracking,
            })

            return
        }

        window.analytics.identify(userData?.id, {
            ...userData,
            ...OCSessionData(),
        })

        if (!delayed) {
            resolve()
        } else {
            // Slight delay so the identify request has more change of success.
            setTimeout(() => {
                resolve()
            }, 100)
        }
    })
}

export const identifySystemUser = () => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    window.analytics.identify('SystemUser', {
        ...OCSessionData(),
        // overwrite  session data forlogged out
        realUserId: 'SystemUser',
        accountId: 'SystemUser',
    })
}

export const trackAllDynamicFilters = (activeFilters, eventCategoryLabel) => {
    // method is executed once, on inital cold pageload.
    const trackingAggregation = {
        ...(activeFilters.tags && { Value: [] }),
        ...(activeFilters.country && { Country: [] }),
        General: [],
    }

    if (window.Store.isExcludedFromTracking || !activeFilters) {
        return false
    }

    if (Object.keys(activeFilters.curatedLists).length) {
        // aggregate all curated filters
        Object.entries(activeFilters.curatedLists).map((filter) => {
            const filterName = filter[0]
            const filterValues = filter[1]
            if (!trackingAggregation[filterName]) {
                trackingAggregation[filterName] = []
            }
            filterValues.map((item) => {
                trackingAggregation[filterName].push(item)
            })
        })
    }

    // aggregate multivalue filters
    if (activeFilters.discounts) {
        trackingAggregation.General.push(`with-discounts`)
    }
    if (activeFilters.new_brands) {
        trackingAggregation.General.push(`new-brands`)
    }
    if (activeFilters.new_products) {
        trackingAggregation.General.push(`new-products`)
    }
    if (activeFilters.bestsellers) {
        trackingAggregation.General.push(`bestsellers`)
    }

    for (const tag of activeFilters.tags) {
        trackingAggregation.Value.push(tag.toLowerCase().replace('_', '-'))
    }
    for (const country of activeFilters.country) {
        trackingAggregation.Country.push(country)
    }

    Object.entries(trackingAggregation).map((arr) => {
        if (arr[1].length) {
            trackDynamicFilter(
                arr[0].replace('f_', '').replace('fs_', ''),
                arr[1],
                eventCategoryLabel,
                'Filter Active',
                'Preset'
            )
        }
    })

    // add single value filters
    if (activeFilters.leadTime) {
        let label = ''

        // The leadtime is just a string when when the page loads otherwise we keep the state as an array.
        if (isArray(activeFilters.leadTime)) {
            label = activeFilters.leadTime[0].split('-')[1]
        } else {
            label = activeFilters.leadTime.split('-')[1]
        }

        trackDynamicFilter(
            'Time to Ship',
            label ? `<${label[1]} days` : `>${label[0]} days`,
            eventCategoryLabel,
            'Filter Active',
            'Preset'
        )
    }
    if (activeFilters.minimum) {
        let label = ''

        // The minimum is just a string when when the page loads otherwise we keep the state as an array.
        if (isArray(activeFilters.minimum)) {
            label = activeFilters.minimum[0].split('-')[1]
        } else {
            label = activeFilters.minimum.split('-')[1]
        }

        trackDynamicFilter(
            'Minimum Order',
            `<${label} €`,
            eventCategoryLabel,
            'Filter Active',
            'Preset'
        )
    }
    if (activeFilters.msrpMax && activeFilters.msrpMin) {
        trackDynamicFilter(
            'Price Range',
            `${activeFilters.msrpMin}-${activeFilters.msrpMax} `,
            eventCategoryLabel,
            'Filter Active',
            'Preset'
        )
    }
}

export const trackDynamicFilter = (
    filterType,
    filterValue,
    eventCategoryLabel,
    eventActionType = 'Filter Active',
    eventMethod = 'Selected'
) => {
    if (window.Store.isExcludedFromTracking || filterValue === '') {
        return false
    }
    // method is executed on click of inactive filter.
    const eventCategory = eventCategoryLabel
    const eventAction = `${eventActionType} - ${filterType}`
    const routeDepth = window?.Store?.currentCategoryDepth

    const properties = {
        event: eventAction,
        category: eventCategory,
        filterLabel: filterType,
        filterValue,
        query: filterValue,
        methodSet: eventMethod,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackStorefrontClick = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    const routeDepth = window?.Store?.currentCategoryDepth

    const properties = {
        event: payload.action,
        category: payload.category,
        view: payload.viewType,
        recommended: payload.isRecommended ? 'yes' : 'no',
        ...(payload.index && { position: payload.index }),
        ...(payload.productname && { productname: payload.productname }),
        ...(payload.brand && { brandname: payload.brand }),
        ...(payload.requestId && { requestId: payload.requestId }),
        ...(payload.listingId && { listingId: payload.listingId }),
        ...(payload.storefrontId && { storefrontId: payload.storefrontId }),
        ...(payload.viewerCountry && { viewercountry: payload.viewerCountry }),
        ...(payload.mainCategory && { mainCategory: payload.mainCategory }),
        ...(payload.sub && { sub: payload.sub }),
        ...(payload.subsub && { subsub: payload.subsub }),
        ...(payload.filtersQuery && { filtersQuery: payload.filtersQuery }),
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(payload.action, properties)
    }
}

export const trackReviews = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    const properties = {
        event: payload.action,
        category: 'Reviews',
        ...(payload.brand && { brand: payload.brand }),
        ...(payload.rating && { rating: payload.rating }),
        ...(payload.orderNumber && { order: payload.orderNumber }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(payload.action, properties)
    }
}

export const trackReviewProducts = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    const properties = {
        event: payload.action,
        category: payload.category,
        brand: payload.brand,
        ...(payload.product && { product: payload.product }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(payload.action, properties)
    }
}

export const trackStorefrontOnMarketplaceClick = (isRecommended) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const routeDepth = window?.Store?.currentCategoryDepth

    const eventCategory = 'Marketplace'
    const eventAction = isRecommended
        ? 'Click Storefront Recommendation'
        : 'Click Storefront List'
    const properties = {
        event: eventAction,
        category: eventCategory,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackHeaderClick = ({ type, name }) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const eventCategory = 'Header'
    let eventAction = ''
    if (type) {
        if (type === 'Header') {
            eventAction = `Click Category Main`
        } else if (type === 'Menu') {
            eventAction = `Click Category Dropdown`
        } else {
            eventAction = `Click ${name}`
        }
    } else {
        eventAction = `Click ${name}`
    }
    const properties = {
        event: eventAction,
        category: eventCategory,
        ...(type && { label: name }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackFeaturedMenuClick = ({
    label,
    analyticsSection,
    analyticsName,
}) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    const eventCategory = 'Header'
    const eventAction = `Click Featured Dropdown - ${analyticsSection}`

    const properties = {
        event: eventAction,
        category: eventCategory,
        label,
        analyticsSection,
        analyticsName,
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackSearchEvents = (eventName, payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const eventCategory = 'Header'
    const searchEventActions = {
        showSuggestion: 'Search - Show Suggestions',
        clickSuggestion: 'Search - Click Suggestion',
        goToSearch: 'Search - Go to full results',
    }

    const idMapping = {
        brand: 'storefrontId',
        category: 'categoryId',
        page: 'pageId',
        product: 'listingId',
    }

    const suggestionId = idMapping?.[payload?.type] || 'id'
    if (searchEventActions[eventName]) {
        const properties = {
            event: searchEventActions[eventName],
            category: eventCategory,
            discoveryPath: getDiscoveryCookies().discoveryPath,
            ...(payload?.query && {
                query: payload?.query || 'error in query',
            }),
            ...(payload && {
                name: payload?.name,
                [suggestionId]: payload?.id,
                suggestionType: payload?.type,
            }),
            ...OCSessionData(),
        }

        if (window.Store.accountType) {
            window.analytics.track(searchEventActions[eventName], properties)
        }
    }
}

export const trackCartEvents = (eventName) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const eventCategory = 'Cart'
    const cartEventActions = {
        bottomContinueShopping: 'Continue shopping bottom',
        checkoutSeperately: 'Clicked checkout separately',
        checkoutSeperatelyON: 'Selected a separate checkout',
        checkoutSeperatelyOFF: 'Unselected a separate checkout',
        buyerProtection: 'Click Buyer Protection',
        changedByStock: 'Quantity changed - stock changed',
        deleteCartItem: 'Delete product from Cart',
        emptyCart: 'Empty Cart',
        FAQ: 'Click FAQ',
        favoriteAdded: 'Added to Favorite',
        removedByStock: 'Product removed - out of stock',
        saveForLater: 'Remove after Favoriting',
        sidebarContinueShopping: 'Continue shopping right',
    }

    if (cartEventActions[eventName]) {
        const properties = {
            event: cartEventActions[eventName],
            category: eventCategory,
            ...OCSessionData(),
        }

        if (window.Store.accountType) {
            window.analytics.track(cartEventActions[eventName], properties)
        }
    }
}

export const trackAddToCart = (
    mode,
    page,
    variantType = '',
    extraPayload = {}
) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    const eventAction = `${mode ? mode + ' ' : ''}Add to Cart${variantType}`
    const properties = {
        event: eventAction,
        category: page,
        ...extraPayload,
        ...getDiscoveryCookies(),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}
export const trackAddToCartBulk = (mode, page, extraPayload = {}) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const eventAction = `${mode} Add to Cart Bulk`
    const properties = {
        event: eventAction,
        category: page,
        ...extraPayload,
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackDeadEnd = (
    eventName,
    location = 'Marketplace',
    level,
    query
) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const routeDepth = window?.Store?.currentCategoryDepth

    const actions = {
        isOutOfStock: 'Out of Stock Product Viewed',
        storefrontNoResults: 'Zero Products shown',
        marketplaceNoResults: 'Zero Brands Shown',
        listingsNoResults: 'Zero Products shown',
        searchNoResults: 'Zero Products shown',
        wallNoResults: 'Zero Products shown',
    }

    const eventAction = actions[eventName]

    if (!eventAction) {
        return
    }

    const properties = {
        event: eventAction,
        category: location,
        level,
        query,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }
    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackPaa = (eventName) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const actions = {
        available: 'Net60 Available',
        limitReached: 'Net60 Limit Reached',
        partial: 'Net60 Partial',
    }
    const eventAction = actions[eventName]
    const properties = {
        event: eventAction,
        category: 'Checkout',
        ...OCSessionData(),
    }
    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackStorefront = (eventName) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const actions = {
        brandpage: 'Click to Brandpage',
        shoppage: 'Click to Shoppage',
        sidecartGoToCart: 'Sticky Cart - Click to Cart',
        sidecartUpdateQuantity: 'Sticky Cart - Change quantity',
        sidecartRemoveProduct: 'Sticky Cart - Remove product',
        sidecartOpenSwitch: 'Sticky Cart - Clicked Switch',
        sidecartChangeStorefront: 'Sticky Cart - Changed Storefront',
    }
    const eventAction = actions[eventName]
    const properties = {
        event: eventAction,
        category: 'Storefront',
        ...OCSessionData(),
    }
    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackMarketplace = (eventName, eventProperties) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const routeDepth = window?.Store?.currentCategoryDepth

    const properties = {
        event: eventName,
        category: 'Marketplace',
        ...eventProperties,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventName, properties)
    }
}

export const trackProductMarketplace = (
    eventName,
    eventProperties,
    eventLocation = 'Marketplace'
) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const routeDepth = window?.Store?.currentCategoryDepth

    const properties = {
        event: eventName,
        category: eventLocation,
        ...eventProperties,
        ...(routeDepth && { depth: routeDepth - 1 }),
        ...OCSessionData(),
    }

    if (window.Store.accountType) {
        window.analytics.track(eventName, properties)
    }
}

export const trackModals = (eventName, additionalProperties = {}) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const actions = {
        voucherPrompt: 'Voucher Popup Seen',
        voucherPromptClose: 'Voucher Popup Closed',
        ocPlus: 'OC Plus Modal Seen',
        odf: 'ODF Modal Seen',
        odfCtaClicked: 'ODF Modal CTA Clicked',
        welcome: 'Welcome Modal Seen',
        connectAndGrowCalendarSeen: 'Calender Invite Modal Shown',
        calendarClicked: 'Calender Invite Added',
        'bnpl-checkout': 'BNPL checkout modal seen',
        lowMinimumEventModalSeen: 'Low minimum event modal seen',
        lowMinimumEventModalAttend: 'Low minimum event modal attend clicked',
        connectAndGrowEventModalSeen: 'Connect and grow event modal seen',
        connectAndGrowEventModalAttend:
            'Connect and grow event modal attend clicked',
        OcPlusPayment: 'OC Plus Payment Modal Seen',
    }

    const eventAction = actions[eventName]
    const properties = {
        event: eventAction,
        category: 'Modal',
        ...additionalProperties,
        ...OCSessionData(),
    }
    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackTooltip = (eventName) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    const actions = {
        minimumStoreFrontOrder:
            'Minimum Storefront Order Education Tooltip Seen',
        hotDealsFilter: 'Hot Deals Filter Education Tooltip Seen',
        starSellerFilter: 'Star Seller Filter Education Tooltip Seen',
        dropshippingFullReturnsFilter:
            'DS full retruns only filter Education Tooltip Seen',
        msrpProductCard: 'Msrp Product Card Education Tooltip Seen',
        followBrand: 'Follow Brand Education Tooltip Seen',
        msrpProduct: 'Msrp Product Education Tooltip Seen',
        starSellerBadge: 'Star Seller Badge Education Tooltip Seen',
        chatBrand: 'Chat Brand Education Tooltip Seen',
        bnplPayment: 'BNPL Payment Education Tooltip Seen',
        vatCheckout: 'VAT Checkout label Education Tooltip Seen',
        ticaFilter: 'TICA Filter Education Tooltip Seen',
        ticaPickUp: 'TICA Pick Up Education Tooltip Seen',
        voucherInCart: 'Voucher In Cart Education Tooltip Seen',
        shippingCost: 'Shipping Cost Education Tooltip Seen',
        checkoutBrands: 'Checkout Brands Education Tooltip Seen',
        minimumCartOrder: 'Minimum Cart Order Education Tooltip Seen',
        refundMutationDetails: 'Refund Mutation Details Education Tooltip Seen',
        purchasing: 'Purchasing Education Tooltip Seen',
        dropshipping: 'Dropshipping Education Tooltip Seen',
        dropshippingImport: 'Dropshipping Import Education Tooltip Seen',
    }

    const eventAction = actions[eventName]
    const properties = {
        event: eventAction,
        category: 'Tooltip',
        ...OCSessionData(),
    }
    if (window.Store.accountType) {
        window.analytics.track(eventAction, properties)
    }
}

export const trackSliderClick = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }

    const eventAction = `Click Slider ${payload?.place}`
    let eventLabel

    if (typeof payload.itemNumber === 'number') {
        eventLabel = `Item ${payload.itemNumber + 1}`
    }

    if (payload.label) {
        eventLabel = payload.label
    }

    const properties = {
        event: eventAction,
        category: payload.page || '',
        sliderName: payload?.sliderName || payload?.listName || payload?.action,
        sliderId: payload?.sliderId,
        action: payload?.action,

        ...(payload.listName && { listName: payload.listName }),
        ...(payload.storefrontName && { storefront: payload.storefrontName }),
        ...(payload.storefrontId && { storefrontId: payload.storefrontId }),
        ...(payload.productName && { product: payload.productName }),
        ...(payload.productId && { listingId: payload.productId }),
        ...(payload.localized && { localized: payload.localized }),
        ...(payload.newRetailer && { newRetailer: payload.newRetailer }),
        ...(payload.requestId && { requestId: payload.requestId }),
        ...(payload.filterValueList && {
            filterValueList: payload.filterValueList,
        }),
        ...(payload.filterValueListSlug && {
            filterValueListSlug: payload.filterValueListSlug,
        }),
        ...(payload.sliderType && { sliderType: payload.sliderType }),
        ...(eventLabel && { label: eventLabel }),
        ...OCSessionData(),
    }
    if (window.Store.accountType) {
        if (payload.action) {
            // only available on home page for now.
            window.analytics.track(eventAction, properties)
        }
    }
}

export const trackValidationError = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    window.analytics.track('User Validation Error', {
        locale: window.Store?.locale || 'unknown',
        ...payload,
        ...OCSessionData(),
    })
}

export const trackValidationWarning = (payload) => {
    if (window.Store.isExcludedFromTracking) {
        return false
    }
    window.analytics.track('User Validation Warning', {
        locale: window.Store?.locale || 'unknown',
        ...payload,
        ...OCSessionData(),
    })
}
